// #6/#8: Stock Transfer — create transfers between locations and move them
// through the Pending -> Approved -> Transferred -> Received -> Completed flow.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  IconButton,
  CircularProgress,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import RefreshIcon from '@mui/icons-material/Refresh';
import { toast } from 'react-toastify';
import config from '../../config';

const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
const user = JSON.parse(sessionStorage.getItem('user') || '{}');
const token = sessionStorage.getItem('token');
const auth = { Authorization: `Bearer ${token}` };

const LOCATIONS = [
  'Store',
  'Warehouse',
  'Kitchen',
  'Restaurant',
  'Bar',
  'Laundry',
  'Housekeeping',
  'Supplier',
];
const UNITS = ['pcs', 'kg', 'gm', 'ltr', 'ml', 'box', 'pkt'];
const FLOW = ['Pending', 'Approved', 'Transferred', 'Received', 'Completed'];
const NEXT_LABEL = {
  Pending: 'Approve',
  Approved: 'Transfer',
  Transferred: 'Receive',
  Received: 'Complete',
};
const STATUS_COLOR = {
  Pending: 'warning',
  Approved: 'info',
  Transferred: 'primary',
  Received: 'secondary',
  Completed: 'success',
  Cancelled: 'error',
};

export default function StockTransfer() {
  const [items, setItems] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('All');

  // create form
  const [fromLoc, setFromLoc] = useState('Store');
  const [toLoc, setToLoc] = useState('Kitchen');
  const [remarks, setRemarks] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [supplierName, setSupplierName] = useState('');
  const [lines, setLines] = useState([{ itemId: '', qty: '', unit: 'pcs' }]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!company?.id) return;
    setLoading(true);
    const qs = statusFilter !== 'All' ? `?status=${statusFilter}` : '';
    fetch(`${config.API_BASE_URL}/stocktransfer/list/${company.id}${qs}`, { headers: auth })
      .then((r) => r.json())
      .then((d) => setRows(Array.isArray(d) ? d : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => {
    // Transfers move STORE stock items (raw materials with a real CurrentStock),
    // not sellable menu items — so pull from the store master.
    fetch(`${config.API_BASE_URL}/Home/GetStoreItems/${company?.id}`, { headers: auth })
      .then((r) => r.json())
      .then((d) => {
        const list = Array.isArray(d) ? d : d?.data || [];
        setItems(
          list.map((x) => ({
            id: x.id ?? x.Id,
            name: x.storeName ?? x.StoreName ?? x.name ?? '',
            unit: x.unit ?? x.Unit ?? 'pcs',
            currentStock: x.currentStock ?? x.CurrentStock ?? 0,
          })),
        );
      })
      .catch(() => setItems([]));
      fetch(`${config.API_BASE_URL}/Home/GetSuppliers/${company?.id}`, { headers: auth })
        .then((r) => r.json())
        .then((d) => setSuppliers(Array.isArray(d) ? d : d?.data || []))
        .catch(() => setSuppliers([]));
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // #4/#6: per-location stock balances ("where is my stock").
  const [balances, setBalances] = useState([]);
  const [balLoc, setBalLoc] = useState('All');
  const [balShown, setBalShown] = useState(false);
  const loadBalances = useCallback(() => {
    if (!company?.id) return;
    const qs = balLoc && balLoc !== 'All' ? `?location=${encodeURIComponent(balLoc)}` : '';
    fetch(`${config.API_BASE_URL}/stocktransfer/location-balances/${company.id}${qs}`, {
      headers: auth,
    })
      .then((r) => r.json())
      .then((d) => setBalances(Array.isArray(d) ? d : []))
      .catch(() => setBalances([]));
    setBalShown(true);
  }, [balLoc]);

  const setLine = (i, patch) =>
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, { itemId: '', qty: '', unit: 'pcs' }]);
  const delLine = (i) => setLines((ls) => ls.filter((_, idx) => idx !== i));

  const create = async () => {
    const validLines = lines.filter((l) => l.itemId && Number(l.qty) > 0);
    if (validLines.length === 0) {
      toast.error('Add at least one item with quantity');
      return;
    }
    if (fromLoc === toLoc) {
      toast.error('From and To location must differ');
      return;
    }
    if (toLoc === 'Supplier' && !supplierName) {
      toast.error('Please select a supplier');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/stocktransfer/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify({
          CompanyId: company?.id,
          FromLocation: fromLoc,
          ToLocation: toLoc === 'Supplier' && supplierName ? supplierName : toLoc,
          Remarks: remarks,
          UserId: user?.id || user?.Id || 0,
          UserName: user?.username || user?.Username || user?.name || '',
          Items: validLines.map((l) => {
            const it = items.find((x) => x.id === Number(l.itemId));
            return {
              ItemId: Number(l.itemId),
              ItemName: it?.name || '',
              Qty: Number(l.qty),
              Unit: l.unit,
            };
          }),
        }),
      });
      const d = await res.json();
      if (!res.ok || d?.status !== 'success') throw new Error();
      toast.success(`Transfer ${d.transferNo} created`);
      setLines([{ itemId: '', qty: '', unit: 'pcs' }]);
      setRemarks('');
      load();
    } catch {
      toast.error('Failed to create transfer');
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (row, status) => {
    try {
      const res = await fetch(`${config.API_BASE_URL}/stocktransfer/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify({
          Id: row.id,
          Status: status,
          UserId: user?.id || user?.Id || 0,
          UserName: user?.username || user?.Username || user?.name || '',
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.message || 'Failed');
      toast.success(`Moved to ${d.newStatus}`);
      load();
    } catch (e) {
      toast.error(e.message || 'Failed to change status');
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete transfer ${row.transferNo}?`)) return;
    try {
      const res = await fetch(`${config.API_BASE_URL}/stocktransfer/${row.id}`, {
        method: 'DELETE',
        headers: auth,
      });
      if (!res.ok) throw new Error();
      toast.success('Deleted');
      setRows((r) => r.filter((x) => x.id !== row.id));
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f4f6fb', minHeight: '100vh' }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          p: { xs: 2.5, md: 3 },
          mb: 3,
          color: '#fff',
          background:
            'linear-gradient(135deg,#0b1120 0%,#0f172a 40%,#1e3a8a 100%)',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}>
        <SwapHorizIcon sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant='h5' fontWeight={800} sx={{ color: '#fff' }}>Stock Transfer</Typography>
          <Typography sx={{ opacity: 0.85, color: '#fff' }}>
            Store · Warehouse · Kitchen · Restaurant · Bar · Laundry · Housekeeping · Supplier
          </Typography>
        </Box>
      </Paper>

      {/* Create */}
      <Paper elevation={0} sx={{ borderRadius: 3, p: 2.5, mb: 3, border: '1px solid #e8eef7' }}>
        <Typography fontWeight={800} sx={{ mb: 2 }}>New Transfer</Typography>
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={6} md={3}>
            <TextField select fullWidth size='small' label='From' value={fromLoc}
              onChange={(e) => setFromLoc(e.target.value)}>
              {LOCATIONS.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField select fullWidth size='small' label='To' value={toLoc}
              onChange={(e) => setToLoc(e.target.value)}>
              {LOCATIONS.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
            </TextField>
            {toLoc === 'Supplier' && (
              <TextField select fullWidth size='small' label='Supplier'
                sx={{ mt: 1 }}
                value={supplierName}
                onChange={(e) => setSupplierName(e.target.value)}>
                <MenuItem value=''>select supplier</MenuItem>
                {suppliers.map((s) => {
                  const nm = s.supplierName ?? s.SupplierName ?? s.name ?? s.Name ?? '';
                  return (<MenuItem key={s.id ?? s.Id ?? nm} value={nm}>{nm}</MenuItem>);
                })}
              </TextField>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth size='small' label='Remarks' value={remarks}
              onChange={(e) => setRemarks(e.target.value)} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 1.5 }} />
        {lines.map((l, i) => (
          <Grid container spacing={1} key={i} sx={{ mb: 1 }} alignItems='center'>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth size='small' label='Item' value={l.itemId}
                onChange={(e) => {
                  const it = items.find((x) => x.id === Number(e.target.value));
                  setLine(i, { itemId: e.target.value, unit: it?.unit || l.unit });
                }}>
                <MenuItem value=''>— Select —</MenuItem>
                {items.map((it) => (
                  <MenuItem key={it.id} value={it.id}>
                    {it.name} (stock: {it.currentStock} {it.unit})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={5} md={3}>
              <TextField type='number' fullWidth size='small' label='Qty' value={l.qty}
                onChange={(e) => setLine(i, { qty: e.target.value })} />
            </Grid>
            <Grid item xs={5} md={2}>
              <TextField select fullWidth size='small' label='Unit' value={l.unit}
                onChange={(e) => setLine(i, { unit: e.target.value })}>
                {UNITS.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={2} md={1}>
              <IconButton color='error' onClick={() => delLine(i)} disabled={lines.length === 1}>
                <DeleteOutlineIcon />
              </IconButton>
            </Grid>
          </Grid>
        ))}
        <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
          <Button startIcon={<AddIcon />} onClick={addLine}>Add Item</Button>
          <Button variant='contained' onClick={create} disabled={saving}
            sx={{ borderRadius: 2, fontWeight: 700, ml: 'auto' }}>
            {saving ? 'Saving…' : 'Create Transfer'}
          </Button>
        </Box>
      </Paper>

      {/* List */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e8eef7' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2 }}>
          <Typography fontWeight={800}>Transfer History</Typography>
          <TextField select size='small' label='Status' value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 150, ml: 'auto' }}>
            {['All', ...FLOW, 'Cancelled'].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          <IconButton onClick={load}><RefreshIcon /></IconButton>
        </Box>
        <TableContainer sx={{ maxHeight: 520 }}>
          <Table stickyHeader size='small'>
            <TableHead>
              <TableRow>
                {['Transfer #', 'From', 'To', 'Items', 'Status', 'By', 'Date', 'Actions'].map((h) => (
                  <TableCell key={h} sx={{ bgcolor: '#0a2540', color: '#fff', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} align='center' sx={{ py: 5 }}><CircularProgress size={26} /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={8} align='center' sx={{ py: 5, color: '#94a3b8' }}>No transfers.</TableCell></TableRow>
              ) : (
                rows.map((r) => {
                  const next = NEXT_LABEL[r.status];
                  const nextStatus = FLOW[FLOW.indexOf(r.status) + 1];
                  return (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{r.transferNo}</TableCell>
                      <TableCell>{r.fromLocation}</TableCell>
                      <TableCell>{r.toLocation}</TableCell>
                      <TableCell align='center'>{r.itemCount}</TableCell>
                      <TableCell>
                        <Chip size='small' color={STATUS_COLOR[r.status] || 'default'} label={r.status} />
                      </TableCell>
                      <TableCell>{r.transferByName || '—'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-IN') : '—'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {next && (
                          <Button size='small' variant='contained' sx={{ mr: 0.5 }}
                            onClick={() => changeStatus(r, nextStatus)}>
                            {next}
                          </Button>
                        )}
                        {r.status !== 'Completed' && r.status !== 'Cancelled' && (
                          <Button size='small' color='error' onClick={() => changeStatus(r, 'Cancelled')}>
                            Cancel
                          </Button>
                        )}
                        <IconButton size='small' color='error' onClick={() => remove(r)}>
                          <DeleteOutlineIcon fontSize='small' />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* #4/#6: Location Stock — live per-location balances moved by transfers */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e8eef7', mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, flexWrap: 'wrap' }}>
          <Typography fontWeight={800}>Location Stock</Typography>
          <TextField select size='small' label='Location' value={balLoc}
            onChange={(e) => setBalLoc(e.target.value)} sx={{ minWidth: 160, ml: 'auto' }}>
            {['All', ...LOCATIONS].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          <Button variant='outlined' startIcon={<RefreshIcon />} onClick={loadBalances}
            sx={{ borderRadius: 2, fontWeight: 700 }}>
            Show
          </Button>
        </Box>
        {balShown && (
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table stickyHeader size='small'>
              <TableHead>
                <TableRow>
                  {['Item', 'Location', 'Qty', 'Unit', 'Updated'].map((h) => (
                    <TableCell key={h} sx={{ bgcolor: '#0a2540', color: '#fff', fontWeight: 700 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {balances.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align='center' sx={{ py: 4, color: '#94a3b8' }}>
                      No location stock yet. Complete a transfer (mark it Received) to populate balances.
                    </TableCell>
                  </TableRow>
                ) : (
                  balances.map((b, i) => (
                    <TableRow key={`${b.storeItemId}-${b.location}-${i}`} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{b.itemName}</TableCell>
                      <TableCell>
                        <Chip size='small' label={b.location} />
                      </TableCell>
                      <TableCell align='right' sx={{ fontWeight: 700, color: b.qty < 0 ? '#c0392b' : 'inherit' }}>
                        {b.qty}
                      </TableCell>
                      <TableCell>{b.unit}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {b.updatedAt ? new Date(b.updatedAt).toLocaleString('en-IN') : '—'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
